/**
 * Campaign Store — Marketing campagnes en journeys
 *
 * Haalt campagne/journey configs op uit de API (YAML-based).
 * Read-only dashboard — alle acties gaan via CLI/agent.
 */

import { create } from 'zustand';
import { supabase } from '../lib/supabase';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface CampaignTemplate {
  id: string;
  file: string;
  subject: string;
  phase: string;
}

export interface Campaign {
  key: string;
  name: string;
  brand: string;
  status: 'planning' | 'active' | 'completed' | 'paused';
  from: string;
  templates: CampaignTemplate[];
  promo: {
    code: string;
    discount: string;
    valid_until: string;
    booking_url?: string;
  } | null;
}

export interface Journey {
  key: string;
  name: string;
  brand: string;
  status: 'draft' | 'active' | 'paused';
  steps: number;
  trigger: string;
}

export interface Guardrails {
  sending: {
    max_emails_per_contact_per_week: number;
    max_emails_per_contact_per_month: number;
    preferred_send_time: string;
    preferred_send_days: string[];
  };
  approval: {
    broadcast_auto_approve_max: number;
    broadcast_require_approval_above: number;
  };
}

interface CampaignState {
  campaigns: Campaign[];
  journeys: Journey[];
  guardrails: Guardrails | null;
  isLoading: boolean;
  error: string | null;
  fetchAll: () => Promise<void>;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

async function getToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

async function apiFetch(url: string): Promise<Response> {
  const token = await getToken();
  if (!token) throw new Error('Not authenticated');
  return fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
}

// ─── Store ──────────────────────────────────────────────────────────────────

export const useCampaignStore = create<CampaignState>()((set) => ({
  campaigns: [],
  journeys: [],
  guardrails: null,
  isLoading: false,
  error: null,

  fetchAll: async () => {
    set({ isLoading: true, error: null });

    try {
      const response = await apiFetch('/api/campaigns');
      if (!response.ok) throw new Error(`API error: ${response.status}`);
      const data = await response.json();

      set({
        campaigns: data.campaigns || [],
        journeys: data.journeys || [],
        guardrails: data.guardrails || null,
        isLoading: false,
      });
    } catch (error) {
      console.error('[Campaign] fetchAll error:', error);
      set({
        isLoading: false,
        error: error instanceof Error ? error.message : 'Fout bij laden',
      });
    }
  },
}));
